import { useState, useEffect, useRef } from "react";
import Dashboard          from "./components/Dashboard";
import RequestList        from "./components/RequestList";
import RequestForm        from "./components/RequestForm";
import ModelRegistry      from "./components/ModelRegistry";
import Teams              from "./components/Teams";
import ApiKeys            from "./components/ApiKeys";
import UserStats          from "./components/UserStats";
import Login              from "./components/Login";
import CreditRequests     from "./components/CreditRequests";
import CreditRequestForm  from "./components/CreditRequestForm";
import CreditsDashboard   from "./components/CreditsDashboard";
import UsageDashboard     from "./components/UsageDashboard";
import KeepGrowCut        from "./components/KeepGrowCut";
import { getCreditRequests } from "./api";

// ── SVG icon components ───────────────────────────────────────────────────────
const Icon = {
  Dashboard:   () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>,
  Requests:    () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>,
  ApiKeys:     () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg>,
  Submit:      () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>,
  Teams:       () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  Models:      () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>,
  MyRequests:  () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  Credits:     () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>,
  Usage:       () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="12" width="4" height="9"/><rect x="10" y="7" width="4" height="14"/><rect x="17" y="3" width="4" height="18"/></svg>,
  KeepGrowCut: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="2" x2="12" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/></svg>,
  Bell:        () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
};

// ── Nav definitions per role ──────────────────────────────────────────────────
const ADMIN_NAV = [
  { id: "dashboard",        label: "Dashboard",           icon: Icon.Dashboard  },
  { id: "usage",            label: "Usage & Results",     icon: Icon.Usage      },
  { id: "keepgrowcut",      label: "Keep, grow or cut",   icon: Icon.KeepGrowCut},
  { id: "requests",         label: "All Requests",        icon: Icon.Requests   },
  { id: "apikeys",          label: "API Keys",            icon: Icon.ApiKeys    },
  { id: "submit",           label: "Submit Request",      icon: Icon.Submit     },
  { id: "creditsdashboard", label: "Credits Dashboard",   icon: Icon.Credits    },
  { id: "creditrequests",   label: "Credit Requests",     icon: Icon.Credits    },
  { id: "teams",            label: "Teams",               icon: Icon.Teams      },
  { id: "models",           label: "Model Registry",      icon: Icon.Models     },
];

const USER_NAV = [
  { id: "submit",           label: "Submit Request",      icon: Icon.Submit     },
  { id: "myrequests",       label: "My Requests",         icon: Icon.MyRequests },
  { id: "mykeys",           label: "My API Keys",         icon: Icon.ApiKeys    },
  { id: "requestcredits",   label: "Request Credits",     icon: Icon.Credits    },
  { id: "mycredits",        label: "My Credit Reqs",      icon: Icon.Credits    },
];

const MANAGER_NAV = [
  { id: "submit",           label: "Submit Request",      icon: Icon.Submit     },
  { id: "myrequests",       label: "Requests",            icon: Icon.MyRequests },
  { id: "mykeys",           label: "API Keys",            icon: Icon.ApiKeys    },
  { id: "requestcredits",   label: "Request Credits",     icon: Icon.Credits    },
  { id: "creditsdashboard", label: "Credits Dashboard",   icon: Icon.Credits    },
  { id: "creditrequests",   label: "Credit Requests",     icon: Icon.Credits    },
];

// ── Load / save auth from localStorage ───────────────────────────────────────
function loadAuth() {
  try { return JSON.parse(localStorage.getItem("gov_auth")); }
  catch { return null; }
}
function saveAuth(auth) {
  if (auth) localStorage.setItem("gov_auth", JSON.stringify(auth));
  else      localStorage.removeItem("gov_auth");
}

export default function App() {
  const [auth,              setAuth]              = useState(loadAuth);
  const [page,              setPage]              = useState(() => {
    const a = loadAuth();
    return !a || a.role === "admin" ? "dashboard" : "submit";
  });
  const [refreshKey,        setRefreshKey]        = useState(0);
  const [requestFilters,    setRequestFilters]    = useState({});
  const [selectedRequestId, setSelectedRequestId] = useState(null);

  // Shell UI state
  const [drawerOpen,     setDrawerOpen]     = useState(false);
  const [avatarMenuOpen, setAvatarMenuOpen] = useState(false);
  const [notifOpen,      setNotifOpen]      = useState(false);
  const [notifItems,     setNotifItems]     = useState([]);
  const avatarRef = useRef(null);
  const notifRef  = useRef(null);

  useEffect(() => {
    if (!avatarMenuOpen) return;
    const handler = (e) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target))
        setAvatarMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [avatarMenuOpen]);

  useEffect(() => {
    if (!notifOpen) return;
    const handler = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target))
        setNotifOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [notifOpen]);

  // ── Notifications (pending credit requests for managers/admins) ──
  useEffect(() => {
    if (!auth) return;
    const isAdm = auth.role === "admin";
    const isMgr = auth.role === "manager";
    if (!isAdm && !isMgr) return;

    const params = {};
    if (isMgr && !isAdm) {
      params.manager_email = auth.email;
      params.status = "pending";
    } else {
      // Admin sees both pending and manager_approved needing action
    }

    const fetchNotifs = async () => {
      try {
        const pending = await getCreditRequests(isAdm
          ? { status: "manager_approved" }
          : { status: "pending", manager_email: auth.email }
        );
        setNotifItems(pending.slice(0, 10));
      } catch { /* silent */ }
    };
    fetchNotifs();
  }, [auth]);

  // ── Auth handlers ──
  const handleLogin = (authData) => {
    saveAuth(authData);
    setAuth(authData);
    setPage(authData.role === "admin" ? "dashboard" : "submit");
  };

  const handleLogout = () => {
    saveAuth(null);
    setAuth(null);
    setPage("dashboard");
    setRequestFilters({});
    setSelectedRequestId(null);
    setAvatarMenuOpen(false);
    setDrawerOpen(false);
  };

  // ── Navigation ──
  const navigate = (targetPage, filters = {}, requestId = null) => {
    setRequestFilters(filters);
    setSelectedRequestId(requestId);
    setPage(targetPage);
    setDrawerOpen(false);
  };

  const viewRequest = (id) => navigate(
    auth?.role === "admin" ? "requests" : "myrequests",
    {},
    id
  );

  // ── Login screen ──
  if (!auth) {
    return (
      <div style={{ minHeight: "100vh", background: "#111111", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Login onLogin={handleLogin} />
      </div>
    );
  }

  const isAdmin   = auth.role === "admin";
  const isManager = auth.role === "manager";
  const isUser    = auth.role === "user";
  const NAV = isAdmin ? ADMIN_NAV : isManager ? MANAGER_NAV : USER_NAV;
  const avatarLetter = auth.name ? auth.name.charAt(0).toUpperCase() : "?";

  return (
    <div className="app-shell">

      {/* ── Top header ────────────────────────────────────────────────────── */}
      <header className="app-header">
        <div className="header-left">
          <button className="hamburger-btn" onClick={() => setDrawerOpen(true)} aria-label="Open navigation">
            <span /><span /><span />
          </button>
          <span className="header-brand">AI Governance</span>
        </div>

        <div className="header-right">
          <div ref={notifRef} style={{ position: "relative" }}>
            <button className="header-icon-btn" aria-label="Notifications"
              onClick={() => setNotifOpen((v) => !v)}
              style={{ position: "relative" }}>
              <Icon.Bell />
              {notifItems.length > 0 && (
                <span style={{
                  position: "absolute", top: 2, right: 2,
                  background: "#ef4444", color: "#fff",
                  fontSize: 10, fontWeight: 700,
                  borderRadius: "50%", width: 16, height: 16,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  lineHeight: 1,
                }}>{notifItems.length > 9 ? "9+" : notifItems.length}</span>
              )}
            </button>

            {notifOpen && (
              <div style={{
                position: "absolute", top: "calc(100% + 8px)", right: 0,
                width: 320, background: "var(--surface, #fff)",
                border: "1px solid var(--border, #e2e8f0)",
                borderRadius: 10, boxShadow: "0 8px 24px rgba(0,0,0,.12)",
                zIndex: 200, overflow: "hidden",
              }}>
                <div style={{
                  padding: "12px 16px", borderBottom: "1px solid var(--border, #e2e8f0)",
                  fontSize: 13, fontWeight: 700,
                }}>
                  {isAdmin ? "Awaiting Final Approval" : "Pending Your Review"}
                </div>
                {notifItems.length === 0 ? (
                  <div className="text-muted" style={{ padding: "16px", fontSize: 13, textAlign: "center" }}>
                    No pending items
                  </div>
                ) : (
                  <div style={{ maxHeight: 280, overflowY: "auto" }}>
                    {notifItems.map((req) => (
                      <div key={req.id}
                        onClick={() => { setNotifOpen(false); navigate(isAdmin ? "creditrequests" : "creditrequests"); }}
                        style={{
                          padding: "10px 16px", cursor: "pointer", fontSize: 13,
                          borderBottom: "1px solid var(--border, #e2e8f0)",
                          transition: "background .1s",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "var(--bg, #f8fafc)"}
                        onMouseLeave={(e) => e.currentTarget.style.background = ""}
                      >
                        <div style={{ fontWeight: 600, marginBottom: 2 }}>{req.project_name}</div>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          {req.requester_name} · {req.credits_requested?.toLocaleString()} credits
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div ref={avatarRef} style={{ position: "relative" }}>
            <button className="avatar-btn" onClick={() => setAvatarMenuOpen((v) => !v)} aria-label="User menu">
              {avatarLetter}
            </button>

            {avatarMenuOpen && (
              <div className="avatar-dropdown">
                <div className="avatar-dropdown-header">
                  <div className={`avatar-dropdown-ava${isAdmin ? " admin" : ""}`}>
                    {avatarLetter}
                  </div>
                  <div>
                    <div className="avatar-dropdown-name">{auth.name}</div>
                    <div className={`avatar-dropdown-role${isAdmin ? " admin" : " user"}`}>
                      {isAdmin ? "Admin" : isManager ? "Manager" : "User"}
                      {isManager && auth.managed_team && (
                        <span style={{ fontWeight: 400, marginLeft: 4, fontSize: 10 }}>
                          · {auth.managed_team.name}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button className="avatar-dropdown-item danger" onClick={handleLogout}>
                  Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ── Nav drawer ────────────────────────────────────────────────────── */}
      {drawerOpen && (
        <div className="drawer-backdrop"
          onClick={(e) => { if (e.target === e.currentTarget) setDrawerOpen(false); }}>
          <nav className="nav-drawer">
            <div className="drawer-header">
              <span className="drawer-brand">AI Governance</span>
              <button className="drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close">✕</button>
            </div>
            <div className="drawer-nav">
              {NAV.map((n) => (
                <a key={n.id} href="#"
                  className={page === n.id ? "active" : ""}
                  onClick={(e) => { e.preventDefault(); navigate(n.id); }}>
                  {n.icon && <span className="drawer-nav-icon"><n.icon /></span>}
                  {n.label}
                </a>
              ))}
            </div>
            <div className="drawer-footer">
              <div className={`drawer-footer-ava${isAdmin ? " admin" : ""}`}>
                {avatarLetter}
              </div>
              <div className="drawer-footer-info">
                <div className="drawer-footer-name">{auth.name}</div>
                <div className="drawer-footer-email">{auth.email}</div>
              </div>
            </div>
          </nav>
        </div>
      )}

      {/* ── Main content ──────────────────────────────────────────────────── */}
      <main className="app-main">
        <div className="content-wrap">

          {/* Admin-only pages */}
          {isAdmin && page === "dashboard" && (
            <Dashboard key={refreshKey} onNavigate={navigate} />
          )}
          {isAdmin && page === "usage" && (
            <UsageDashboard onNavigate={navigate} />
          )}
          {isAdmin && page === "keepgrowcut" && (
            <KeepGrowCut />
          )}
          {isAdmin && page === "requests" && (
            <RequestList
              refresh={refreshKey}
              initialFilters={requestFilters}
              initialSelected={selectedRequestId}
              auth={auth}
            />
          )}
          {isAdmin && page === "apikeys" && (
            <ApiKeys onViewRequest={viewRequest} />
          )}
          {isAdmin && page === "teams"  && <Teams />}
          {isAdmin && page === "models" && <ModelRegistry />}

          {/* Submit (all roles) */}
          {page === "submit" && (
            <>
              <div className="page-header">
                <h1>Submit Request</h1>
                <p>File a new AI use-case for governance review.</p>
              </div>
              <div className="page-body">
                <RequestForm
                  onSuccess={() => setRefreshKey((k) => k + 1)}
                  prefillUser={!isAdmin ? { name: auth.name, email: auth.email } : null}
                />
              </div>
            </>
          )}

          {/* My/Team Requests (user + manager) */}
          {!isAdmin && page === "myrequests" && (
            <RequestList
              refresh={refreshKey}
              initialFilters={{ requester_email: auth.email }}
              initialSelected={selectedRequestId}
              lockedEmail={auth.email}
              pageTitle={isManager ? "Requests" : "My Requests"}
              pageSubtitle="Your submitted AI use-case requests and their governance status."
              isManager={isManager}
              managerEmail={isManager ? auth.email : null}
              teamName={auth.managed_team?.name}
              auth={auth}
            />
          )}

          {/* My API Keys + usage stats (user + manager) */}
          {!isAdmin && page === "mykeys" && (
            <UserStats auth={auth} />
          )}

          {/* ── Credits: dashboard (admin + manager) ─────────────────── */}
          {(isAdmin || isManager) && page === "creditsdashboard" && (
            <CreditsDashboard auth={auth} onNavigate={navigate} />
          )}

          {/* ── Credits: submit form (all roles) ─────────────────────── */}
          {page === "requestcredits" && (
            <CreditRequestForm
              auth={auth}
              onSubmitted={() => navigate("mycredits")}
            />
          )}

          {/* ── Credits: my credit requests (user) ────────────────────── */}
          {page === "mycredits" && (
            <CreditRequests auth={auth} />
          )}

          {/* ── Credits: team review queue (manager + admin) ──────────── */}
          {(isAdmin || isManager) && page === "creditrequests" && (
            <CreditRequests auth={auth} initialStatusTab={requestFilters.status} />
          )}

        </div>
      </main>
    </div>
  );
}
